import React from 'react';
import { useDispatch } from 'react-redux';
import _ from 'lodash';

import { Table, Button } from 'antd';
import { FormOutlined } from '@ant-design/icons';

import { SIZE_CODES } from '../../../../../../utils/constants/size-codes';
import { initWideFormOrderLine, initWideFormProduct, toggleWideFormDrawer } from '../../../../../../slices/WideForm/wideFormSlice';
import { OrderProduct } from '../../../../../../api-client';
import { IProductSizeAggregate } from '../../../../../models/Shopping';
import { sortingDateComparer, sortingNumberComparer, sortingStringComparer } from '../../../../../../utils/data-comparer/data-sorting-comparer';
import { formatCurrency } from '../../../../../../utils/data-format-enchanters/currency-enchanter';
import { formatStandardDate } from '../../../../../../utils/data-format-enchanters/datetime-enchanter';
import {
    aggregateProductSizes,
    groupProductsByProduct,
    orderTotalBySizes,
    orderQuantityBySizes,
    orderLineAmountByProductSize
} from '../../../../../../utils/data-format-enchanters/order-products-enchanter';

import './checkout-table.scss';

interface ICheckoutTable {
    disabled: boolean;
    products: OrderProduct[]
}

const CheckoutTable: React.FunctionComponent<ICheckoutTable> = ({ disabled, products }) => {
    const dispatch = useDispatch();

    const _handleProductSizeClick = (productSizeAggregate: IProductSizeAggregate) => {
        if (disabled) {
            return;
        }

        if (productSizeAggregate.productId && productSizeAggregate.productRowId) {
            dispatch(initWideFormOrderLine(productSizeAggregate.productRowId));
            dispatch(initWideFormProduct(productSizeAggregate.productId));
            dispatch(toggleWideFormDrawer(true));
        }
    }

    const _handleProductsByProductSizes = (orderProduct: OrderProduct) => {
        let currentProductsGroup = _.filter(products, entry => entry.id === orderProduct.id);

        return (
            <Table
                columns={[
                    {
                        title: 'Sap Code',
                        align: 'center',
                        dataIndex: 'sapCode',
                        sorter: (recordA, recordB) => sortingStringComparer(
                            recordA.sapCode,
                            recordB.sapCode
                        )
                    },
                    {
                        title: 'Ship to',
                        align: 'left',
                        dataIndex: 'productFacility',
                        render: (value: any) => `${value.name} ${value.address}, ${value.city}`,
                        sorter: (recordA, recordB) => sortingStringComparer(
                            `${recordA.productFacility?.name} ${recordA.productFacility?.address}, ${recordA.productFacility?.city}`,
                            `${recordB.productFacility?.name} ${recordB.productFacility?.address}, ${recordB.productFacility?.city}`
                        )
                    },
                    {
                        title: 'Delivery Date',
                        align: 'left',
                        dataIndex: 'productDeliveryDate',
                        render: (value: any) => value ? formatStandardDate(value) : '-',
                        sorter: (recordA, recordB) => sortingDateComparer(
                            recordA.productDeliveryDate,
                            recordB.productDeliveryDate
                        )
                    },
                    {
                        title: 'Size',
                        align: 'center',
                        dataIndex: 'code',
                        render: (value: any) => value === SIZE_CODES.NO_SIZE ? '-' : value,
                        sorter: (recordA, recordB) => sortingStringComparer(
                            recordA.code,
                            recordB.code
                        )
                    },
                    {
                        title: 'Unit Price',
                        align: 'right',
                        dataIndex: 'priceNet',
                        render: (value: any) => formatCurrency(value.amount, value.currency),
                        sorter: (recordA, recordB) => sortingNumberComparer(
                            recordA.priceNet?.amount,
                            recordB.priceNet?.amount
                        )
                    },
                    {
                        title: 'Quantity',
                        align: 'center',
                        dataIndex: 'quantity',
                        sorter: (recordA, recordB) => sortingNumberComparer(
                            recordA.quantity,
                            recordB.quantity
                        ),
                        width: 115
                    },
                    {
                        title: 'Amount',
                        align: 'right',
                        render: (value: any) => orderLineAmountByProductSize(value).amountFormat,
                        sorter: (recordA, recordB) => sortingNumberComparer(
                            orderLineAmountByProductSize(recordA).amount,
                            orderLineAmountByProductSize(recordB).amount
                        ),
                        width: 200
                    },
                    {
                        title: '',
                        align: 'center',
                        render: (value: any, dataItem: any) => (
                            !disabled &&
                            <Button
                                type="primary"
                                icon={<FormOutlined />}
                                size={'small'}
                                onClick={() => _handleProductSizeClick(dataItem)}
                            />
                        ),
                        width: 85
                    }
                ]}
                dataSource={aggregateProductSizes(currentProductsGroup)}
                pagination={false}
                rowKey={(itemData: any) => `antd-${itemData._arrayKey}`}
            />
        );
    }

    return (
        <div className="checkout-table-container">
            <Table
                columns={[
                    {
                        title: '',
                        align: 'center',
                        dataIndex: 'imageUrl',
                        render: (value: any, dataItem: any) => <img src={value} className="checkout-table-img" alt={dataItem.name} />
                    },
                    {
                        title: 'Code',
                        align: 'left',
                        dataIndex: 'catalogCode',
                        sorter: (recordA, recordB) => sortingStringComparer(
                            recordA.catalogCode,
                            recordB.catalogCode
                        )
                    },
                    {
                        title: 'L.E. Number',
                        align: 'left',
                        dataIndex: 'limitedEditionCode'
                    },
                    {
                        title: 'Name',
                        align: 'left',
                        dataIndex: 'name',
                        sorter: (recordA, recordB) => sortingStringComparer(
                            recordA.name,
                            recordB.name
                        ),
                    },
                    {
                        title: 'Quantity',
                        align: 'center',
                        dataIndex: 'sizes',
                        render: (value: any) => orderQuantityBySizes(value),
                        sorter: (recordA, recordB) => sortingNumberComparer(
                            orderQuantityBySizes(recordA.sizes),
                            orderQuantityBySizes(recordB.sizes)
                        ),
                        width: 115
                    },
                    {
                        title: 'Amount',
                        align: 'right',
                        dataIndex: 'sizes',
                        render: (value: any) => orderTotalBySizes(value).amountFormat,
                        sorter: (recordA, recordB) => sortingNumberComparer(
                            orderTotalBySizes(recordA.sizes).amount,
                            orderTotalBySizes(recordB.sizes).amount
                        ),
                        width: 200
                    },
                    {
                        title: '',
                        align: 'center',
                        width: 85
                    }
                ]}
                dataSource={groupProductsByProduct(products)}
                expandable={{
                    expandedRowRender: _handleProductsByProductSizes
                }}
                rowKey={(itemData) => `antd-${itemData.rowId}`}
                pagination={false}
            />
        </div>
    );
}

export default CheckoutTable;